/**
 * XML Document Import Service
 * Main service for importing XML document definitions into the design system
 */

import { parseXMLDocumentDefinitions, getSectionContent } from './xmlDocumentParser';
import { getTemplate, lookupBlockByFilename, isCacheInitialized } from './xmlDocumentImportCache';
import { createComplexDocument, updateMetaTags, patchComplexDocument, updateCloneable, updateRenderingType, sendToTesting, moveToQaComplete, publishDesign, getStandaloneBlock } from './services/complexDocumentsAPI';
import { updateBusinessLogic } from './services/businessLogicApi';
import { HTML_BLOCK, STANDALONE_BLOCK, DESIGN_TYPE } from './complexDocumentConstants';
import { v4 } from 'uuid';

const CONCURRENCY_LIMIT = 10;

/**
 * Process an array of items with a limited number of concurrent async workers.
 * @param {Array} items - Items to process
 * @param {number} concurrency - Max parallel workers
 * @param {Function} processFn - Async function(item, index)
 */
const processWithConcurrency = async (items, concurrency, processFn) => {
  let index = 0;
  const runNext = async () => {
    while (index < items.length) {
      const currentIndex = index++;
      await processFn(items[currentIndex], currentIndex);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, () => runNext()),
  );
};

/**
 * Run async work with a global cap so many concurrent workers share one limiter.
 * @param {number} limit - Max concurrent executions
 * @returns {(fn: () => Promise<unknown>) => Promise<unknown>}
 */
const createLimitedRunner = (limit) => {
  let active = 0;
  const queue = [];
  const runNext = () => {
    if (active >= limit || queue.length === 0) return;
    active += 1;
    const { fn, resolve, reject } = queue.shift();
    Promise.resolve()
      .then(fn)
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        runNext();
      });
  };
  return (fn) =>
    new Promise((resolve, reject) => {
      queue.push({ fn, resolve, reject });
      runNext();
    });
};

/**
 * Serialize first-language document creates so each document gets a distinct
 * communicationId / groupId (same race condition as block import).
 */
const runNewDocumentGroup = createLimitedRunner(1);

/**
 * Helper function to recursively remove IDs from blocks
 * @param {Object} block - Block object
 * @returns {Object} Block with IDs removed
 */
const removeBlockIds = (block) => {
  if (!block) return block;
  
  const cleaned = { ...block };
  delete cleaned.blockId;
  delete cleaned.id;
  
  if (cleaned.blockDTOList && Array.isArray(cleaned.blockDTOList)) {
    cleaned.blockDTOList = cleaned.blockDTOList.map(removeBlockIds);
  }
  
  return cleaned;
};

/**
 * Create HTML block structure from content
 * @param {string} content - HTML content
 * @param {string} sectionId - Section ID (pod, body, signature, etc.)
 * @returns {Object} HTML block structure
 */
const createHTMLBlock = (content, sectionId) => {
  return removeBlockIds({
    blockType: HTML_BLOCK,
    blockName: sectionId,
    variableContract: [
      {
        name: `paragraph-${sectionId}`,
        type: 'html',
        content: [],
      },
    ],
    style: {
      height: 100,
      fontFamily: 'Arial',
      fontSize: 13,
      lineSpacing: 1.1,
      paragraphSpacing: 10,
      alignment: 'justify'
    },
    contents: content || '<p style="text-align: left"> <br/> </p>',
    isDeleted: false,
    isEditable: true,
    isStandalone: false,
    blockDTOList: [],
    isTranslated: false,
  });
};

/**
 * Build a referenced block structure from a block data object
 * @param {Object} block - Block data (from API or cache)
 * @returns {Object} Referenced block structure
 */
const buildReferencedBlockStructure = (block) => {
  return {
    blockType: STANDALONE_BLOCK,
    blockName: block.name || block.blockName || block.parentBlockName,
    blockId: block.id || block.blockId || block.parentBlockId,
    isStandalone: true,
    isDeleted: false,
    isEditable: false,
    blockDTOList: block.blockDTOList || block.blocks || [],
    variableContract: block.variableContract || block.variableContractDTOS || [],
    style: {
      ...(block.style || {}),
      fontFamily: 'Arial',
      fontSize: 13,
      lineSpacing: 1.1,
      paragraphSpacing: 10,
      alignment: 'justify'
    },
    isTranslated: false,
  };
};

/**
 * Create referenced block structure by fetching the standalone block from the API.
 * Falls back to the cached block if the API call fails.
 * @param {Object} cachedBlock - Block data from cache (used as fallback and for the block ID)
 * @returns {Promise<Object>} Referenced block structure
 */
const createReferencedBlock = async (cachedBlock) => {
  const blockId = cachedBlock.id || cachedBlock.blockId;
  try {
    const response = await getStandaloneBlock(blockId, undefined, false);
    if (response?.data) {
      return buildReferencedBlockStructure(response.data);
    }
  } catch (error) {
    console.warn(`Failed to fetch standalone block ${blockId}, using cached block:`, error.message);
  }
  return buildReferencedBlockStructure(cachedBlock);
};

/**
 * Find and replace placeholder blocks in a flow
 * Preserves all existing blocks (including referenced blocks) and only replaces placeholder blocks
 * @param {Object} flow - Flow object from template
 * @param {Object} sectionBlocks - Map of sectionId to block structures (can be single block or array)
 * @returns {Object} Updated flow with replaced blocks
 */
const replacePlaceholderBlocks = (flow, sectionBlocks) => {
  const updatedFlow = JSON.parse(JSON.stringify(flow));
  
  if (!updatedFlow.blocks || !Array.isArray(updatedFlow.blocks)) {
    return updatedFlow;
  }
  
  // Build a list of placeholder section IDs that we need to replace
  const placeholderSectionIds = ['pod', 'reference', 'object', 'body', 'signature'];
  
  // Process blocks: replace placeholders, keep all others (including referenced blocks)
  const newBlocks = [];
  let cumulativeOffset = 0; // Tracks total blocks added/removed so far
  
  for (let i = 0; i < updatedFlow.blocks.length; i++) {
    const block = updatedFlow.blocks[i];
    const sectionId = block.blockName;
    const originalOrderNumber = block.orderNumber;
    
    // Check if this is a placeholder block we need to replace
    if (placeholderSectionIds.includes(sectionId) && sectionBlocks.has(sectionId)) {
      const replacement = sectionBlocks.get(sectionId);
      const replacementBlocks = Array.isArray(replacement) ? replacement : [replacement];
      
      // Add replacement blocks with adjusted order numbers
      replacementBlocks.forEach((replacementBlock, index) => {
        newBlocks.push({
          ...replacementBlock,
          orderNumber: originalOrderNumber + index + cumulativeOffset,
          uuid: block.uuid && index === 0 ? block.uuid : v4(),
        });
      });
      
      // Update cumulative offset: if we replaced 1 block with N blocks, offset increases by (N-1)
      cumulativeOffset += replacementBlocks.length - 1;
    } else if (placeholderSectionIds.includes(sectionId) && !sectionBlocks.has(sectionId)) {
      // Empty section - skip this placeholder block
      // This removes 1 block, so offset decreases by 1
      cumulativeOffset -= 1;
    } else {
      // Keep existing block (referenced blocks, etc.) but adjust order number
      newBlocks.push({
        ...block,
        orderNumber: originalOrderNumber + cumulativeOffset,
      });
    }
  }
  
  updatedFlow.blocks = newBlocks;
  
  return updatedFlow;
};

/**
 * Build section blocks map for a language
 * @param {Object} documentDefinition - Parsed document definition
 * @param {string} language - Language code
 * @param {boolean} useReferences - Whether to use referenced blocks (true) or local HTML blocks (false)
 * @returns {Map} Map of sectionId to block structures
 */
const buildSectionBlocks = async (documentDefinition, language, useReferences = true) => {
  const sectionBlocks = new Map();
  const sectionIds = ['pod', 'reference', 'object', 'body', 'signature'];
  
  for (const sectionId of sectionIds) {
    const sectionContent = getSectionContent(documentDefinition, sectionId, language);
    
    if (sectionContent.length === 0) {
      // No content for this section, skip
      continue;
    }
    
    // Combine all blocks in the section
    const blocks = [];
    for (const blockData of sectionContent) {
      // if (blockData.content && /<table[\s>]/i.test(blockData.content)) {
      //   console.warn(`Skipping block with <table> tag in section "${sectionId}" (path: ${blockData.path || '(none)'})`);
      //   continue;
      // }

      const pathParts = (blockData.path || '').split('/').filter((p) => p !== '');
      const filenameWithExtension =
        pathParts.length > 0 ? pathParts[pathParts.length - 1] : '';
      const povc =
        pathParts.length > 1 ? decodeURIComponent(pathParts[1]) : '';

      if (useReferences && filenameWithExtension) {
        const referencedBlock = lookupBlockByFilename(
          povc,
          filenameWithExtension,
          language,
        );
        if (referencedBlock) {
          blocks.push(await createReferencedBlock(referencedBlock));
        } else {
          console.warn(
            `Block with filename ${filenameWithExtension} (povc: ${povc || '(none)'}) not found for language ${language}, using HTML content`,
          );
          if (blockData.content) {
            blocks.push(createHTMLBlock(blockData.content, sectionId));
          }
        }
      } else if (blockData.content) {
        // Create HTML block from content (either no iytxtbs or useReferences is false)
        blocks.push(createHTMLBlock(blockData.content, sectionId));
      }
    }
    
    if (blocks.length > 0) {
      if (blocks.length === 1) {
        sectionBlocks.set(sectionId, blocks[0]);
      } else {
        // Store as array for multiple blocks (already sequential)
        sectionBlocks.set(sectionId, blocks.map((block, index) => ({
          ...block,
          orderNumber: index + 1,
        })));
      }
    }
  }
  
  return sectionBlocks;
};

/**
 * Mutate template for a specific language
 * @param {Object} template - Template document JSON
 * @param {Object} documentDefinition - Parsed document definition
 * @param {string} language - Language code
 * @param {boolean} useReferences - Whether to use referenced blocks (true) or local HTML blocks (false)
 * @returns {Object} Mutated template
 */
const mutateTemplate = async (template, documentDefinition, language, useReferences = true) => {
  const mutated = JSON.parse(JSON.stringify(template));
  
  // Preserve businessLogics before deletion (will be cloned separately)
  const businessLogics = mutated.businessLogics ? JSON.parse(JSON.stringify(mutated.businessLogics)) : null;
  
  // Update document name - use French name only, without language suffix
  const frenchName = documentDefinition.names.fr || 
                     documentDefinition.names.en || 
                     documentDefinition.names.nl || 
                     documentDefinition.names.de || 
                     'Imported Document';
  mutated.name = frenchName;
  mutated.language = language;
  
  // Build section blocks for this language
  const sectionBlocks = await buildSectionBlocks(documentDefinition, language, useReferences);
  
  // Replace placeholder blocks in all flows
  if (mutated.flows && Array.isArray(mutated.flows)) {
    mutated.flows = mutated.flows.map((flow) => replacePlaceholderBlocks(flow, sectionBlocks));
  }
  
  // Remove communicationId, documentId, and id to allow creation
  delete mutated.communicationId;
  delete mutated.documentId;
  delete mutated.id;
  delete mutated.businessLogics; // Remove from mutated template (will be cloned separately)
  
  // Attach businessLogics to mutated object for later use
  mutated._preservedBusinessLogics = businessLogics;
  
  return mutated;
};

/**
 * Convert metadata object to meta tags array
 * Only includes: nameFr, nameNl, povc, fileType, subType
 * @param {Object} metadata - Metadata object from XML
 * @param {Object} documentNames - Document names by language
 * @returns {Array} Array of meta tag objects
 */
const convertMetadataToMetaTags = (metadata, documentNames) => {
  // Map of allowed keys (case-insensitive lookup)
  // XML may have lowercase keys (filetype, subtype) but we want camelCase (fileType, subType)
  const keyMapping = {
    'povc': 'povc',
    'filetype': 'fileType',
    'fileType': 'fileType',
    'subtype': 'subType',
    'subType': 'subType',
  };
  
  const metaTags = [];
  
  // Add nameFr and nameNl from document names
  if (documentNames.fr) {
    metaTags.push({ key: 'nameFr', value: documentNames.fr.trim() });
  }
  if (documentNames.nl) {
    metaTags.push({ key: 'nameNl', value: documentNames.nl.trim() });
  }
  
  // Add allowed metadata fields only (case-insensitive matching)
  for (const [key, value] of Object.entries(metadata)) {
    const normalizedKey = keyMapping[key.toLowerCase()];
    
    if (normalizedKey && value && value.trim() !== '') {
      metaTags.push({
        key: normalizedKey, // Use normalized key (camelCase)
        value: value.trim(),
      });
    }
  }

  metaTags.push({ key: 'type', value: 'letter' });
  
  return metaTags;
};

/**
 * Clone business logic from template and remove all IDs
 * @param {Array} businessLogics - Business logics from template
 * @param {string} documentId - New document ID
 * @returns {Array} Cloned business logics with IDs removed
 */
const cloneBusinessLogic = (businessLogics, documentId) => {
  if (!businessLogics || !Array.isArray(businessLogics) || businessLogics.length === 0) {
    return [];
  }
  
  return businessLogics.map(businessLogic => {
    const cloned = JSON.parse(JSON.stringify(businessLogic));
    
    // Set documentId and remove business logic id
    cloned.documentId = documentId;
    cloned.id = null;
    
    // Process rules
    if (cloned.rules && Array.isArray(cloned.rules)) {
      cloned.rules = cloned.rules.map(rule => {
        // Remove rule id, but retain blockId and flowId as-is
        rule.id = null;
        
        // Process conditions if present
        if (rule.conditions && Array.isArray(rule.conditions)) {
          rule.conditions = rule.conditions.map(condition => {
            // Remove condition id, retain other properties
            condition.id = null;
            return condition;
          });
        }
        
        return rule;
      });
    }
    
    return cloned;
  });
};

/**
 * Publish a document through the approval workflow
 * @param {string} documentId - Document ID to publish
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Object>} Publishing result with success status, stage, and error message
 */
const publishDocument = async (documentId, onProgress) => {
  try {
    // Step 1: Move to QA Ready
    if (onProgress) {
      onProgress({
        publishStage: 'qa-ready',
        documentId,
        publishStatus: 'qa-ready',
      });
    }
    
    const qaReadyResponse = await sendToTesting(documentId, DESIGN_TYPE.DOCUMENT);
    const successStatusCodes = [2601, 728, 1904];
    
    if (!qaReadyResponse || !successStatusCodes.includes(qaReadyResponse?.statusCode)) {
      const errorMessage = qaReadyResponse?.errors?.[0]?.description || qaReadyResponse?.message || 'Failed to move to QA Ready';
      return {
        success: false,
        stage: 'qa-ready',
        errorMessage,
        documentId,
      };
    }

    // Step 2: Move to QA Complete
    if (onProgress) {
      onProgress({
        publishStage: 'qa-complete',
        documentId,
        publishStatus: 'qa-complete',
      });
    }
    
    const qaCompleteResponse = await moveToQaComplete(documentId, DESIGN_TYPE.DOCUMENT);
    if (qaCompleteResponse?.errors && qaCompleteResponse.errors.length > 0) {
      const errorMessage = qaCompleteResponse.errors[0]?.description || 'Failed to move to QA Complete';
      return {
        success: false,
        stage: 'qa-complete',
        errorMessage,
        documentId,
      };
    }

    // Step 3: Publish
    if (onProgress) {
      onProgress({
        publishStage: 'published',
        documentId,
        publishStatus: 'published',
      });
    }
    
    const publishResponse = await publishDesign(documentId, DESIGN_TYPE.DOCUMENT);
    if (publishResponse?.errors && publishResponse.errors.length > 0) {
      const errorMessage = publishResponse.errors[0]?.description || 'Failed to publish';
      return {
        success: false,
        stage: 'published',
        errorMessage,
        documentId,
      };
    }

    return {
      success: true,
      stage: 'published',
      documentId,
    };
  } catch (error) {
    console.error('Publishing error:', error);
    return {
      success: false,
      stage: 'error',
      errorMessage: error.message || 'Unknown publishing error',
      documentId,
    };
  }
};

/**
 * Create first-language document and apply metadata inside the serialized runner.
 * Extracted to a named function to avoid creating closures inside a loop.
 */
const createFirstLanguageDocumentWithMeta = async (
  minimalPayload,
  documentDefinition,
  docResult,
  language,
) => {
  const res = await createComplexDocument(minimalPayload, false);
  if (res.data?.documentId && res.data.communicationId) {
    const metaTags = convertMetadataToMetaTags(documentDefinition.metadata, documentDefinition.names);
    if (metaTags.length > 0) {
      try {
        await updateMetaTags('communication', res.data.communicationId, metaTags);
      } catch (metaError) {
        console.warn(`Failed to set meta tags for ${res.data.communicationId}:`, metaError);
        docResult.errors.push({
          language,
          error: `Meta tags update failed: ${metaError.message}`,
        });
      }
    }
  }
  return res;
};

export const getDocumentName = (documentDefinition, docIndex) => {
  const name = documentDefinition.names.en || documentDefinition.names.fr || `Document ${docIndex + 1}`;
  if (!name || typeof name !== 'string') {
    return 'Unnamed Document';
  }
  const sanitized = name.replace(/[/\\]/g, '-').trim(); // Replace only the forward and backward slashes with a hyphen
  return sanitized === '' ? 'Unnamed Document' : sanitized;
}

/**
 * Import XML document definitions
 * @param {string} xmlContent - XML content string
 * @param {number} resourceGroupId - Resource group ID
 * @param {number} categoryId - Category ID
 * @param {Function} onProgress - Progress callback
 * @param {boolean} useReferences - Whether to use referenced blocks
 * @returns {Promise<Object>} Import results
 */
export const importXMLDocuments = async (xmlContent, resourceGroupId, categoryId, onProgress, useReferences = true) => {
  const result = {
    totalDocuments: 0,
    processedDocuments: 0,
    successfulImports: 0,
    failedImports: 0,
    documentResults: [],
    errors: [],
  };
  
  try {
    // Check cache initialization only if using references
    if (useReferences && !isCacheInitialized()) {
      throw new Error('Cache not initialized. Please initialize cache before importing documents with referenced blocks.');
    }
    
    if (onProgress) {
      onProgress({
        stage: 'parsing',
        message: 'Parsing XML document definitions...',
      });
    }
    
    // Parse XML document definitions
    const documentDefinitions = parseXMLDocumentDefinitions(xmlContent);
    result.totalDocuments = documentDefinitions.length;
    
    if (onProgress) {
      onProgress({
        stage: 'parsing',
        message: `Found ${documentDefinitions.length} document definition(s)`,
        totalDocuments: documentDefinitions.length,
      });
    }
    
    // Process a single document definition (all its languages, sequentially)
    const processDocument = async (docIndex) => {
      const documentDefinition = documentDefinitions[docIndex];
      const docName = getDocumentName(documentDefinition, docIndex);
      
      const docResult = {
        documentName: docName,
        languages: {},
        errors: [],
      };
      
      // Process only languages that have templates available
      const allLanguages = ['fr', 'nl', 'en', 'de'];
      const availableLanguages = allLanguages.filter(lang => {
        const template = getTemplate(lang);
        return template !== null;
      });
      
      if (availableLanguages.length === 0) {
        docResult.errors.push('No templates available for any language');
        result.failedImports++;
        result.processedDocuments++;
        result.documentResults.push(docResult);
        return;
      }
      
      // Track if this is the first language iteration for meta tags update
      let firstCommunicationId = null;
      let firstGroupId = null;
      
      for (const language of availableLanguages) {
        try {
          if (onProgress) {
            onProgress({
              stage: 'importing',
              documentIndex: docIndex + 1,
              totalDocuments: documentDefinitions.length,
              language,
              documentName: docName,
              processedDocuments: result.processedDocuments,
              successfulImports: result.successfulImports,
              failedImports: result.failedImports,
            });
          }
          
          // Get cached template (should exist since we filtered)
          const template = getTemplate(language);
          if (!template) {
            throw new Error(`Template not found for language: ${language}`);
          }
          
          // Mutate template with document content
          const mutatedTemplate = await mutateTemplate(template, documentDefinition, language, useReferences);
          
          // Create minimal document first with a single empty flow (similar to createDocTemplate)
          // Get the first flow from template to use its structure, or create a default one
          const firstFlow = mutatedTemplate.flows && mutatedTemplate.flows.length > 0 
            ? mutatedTemplate.flows[0] 
            : {
                orderNumber: 1,
                flowName: '',
                style: {
                  padding: { left: 0, right: 0, top: 0, bottom: 0 },
                  width: 794,
                  height: 1123,
                  backgroundColor: '#ffffff',
                },
                paperSize: 'a4',
                paperOrientation: 'portrait',
                header: null,
                footer: null,
                blocks: [],
              };
          
          const minimalPayload = {
            documentName: mutatedTemplate.name,
            displayName: mutatedTemplate.name,
            language: mutatedTemplate.language,
            categoryId: categoryId,
            resourceGroupId: resourceGroupId,
            style: mutatedTemplate.style || {
              fontFamily: 'Arial',
              fontSize: 13,
            },
            flows: [
              {
                orderNumber: firstFlow.orderNumber || 1,
                flowName: firstFlow.flowName || '',
                style: firstFlow.style || {
                  padding: { left: 0, right: 0, top: 0, bottom: 0 },
                  width: 794,
                  height: 1123,
                  backgroundColor: '#ffffff',
                },
                paperSize: firstFlow.paperSize || 'a4',
                paperOrientation: firstFlow.paperOrientation || 'portrait',
                header: firstFlow.header || null,
                footer: firstFlow.footer || null,
                blocks: [],
              },
            ],
          };
          delete minimalPayload.communicationId;
          delete minimalPayload.documentId;
          delete minimalPayload.id;
          
          // After first iteration (when firstCommunicationId exists), include communicationId, newCommunicationId, and groupId
          // This ensures subsequent language documents are linked to the first communication and group
          if (firstCommunicationId) {
            minimalPayload.communicationId = firstCommunicationId;
            minimalPayload.newCommunicationId = firstCommunicationId;
          }
          if (firstGroupId) {
            minimalPayload.groupId = firstGroupId;
          }
          
          // Serialize first-language creates so each document gets a distinct communicationId/groupId
          let createResponse;
          if (firstCommunicationId) {
            createResponse = await createComplexDocument(minimalPayload, false);
          } else {
            createResponse = await runNewDocumentGroup(
              createFirstLanguageDocumentWithMeta.bind(null, minimalPayload, documentDefinition, docResult, language),
            );
          }
          
          if (createResponse.data?.documentId) {
            const documentId = createResponse.data.documentId;
            const communicationId = createResponse.data.communicationId;
            const flowId = createResponse.data.flowId; // Get flowId from create response
            const groupId = createResponse.data.groupId; // Get groupId from create response
            
            // Store first communicationId, documentId, and groupId for subsequent language documents
            if (!firstCommunicationId) {
              firstCommunicationId = communicationId;
              firstGroupId = groupId;
            }
            
            // Update flows with the flowId from create response
            const flowsWithId = mutatedTemplate.flows.map((flow, index) => {
              if (index === 0 && flowId) {
                // Use the flowId from create response for the first flow
                return {
                  ...flow,
                  flowId: flowId,
                };
              }
              return flow;
            });
            
            // Now patch the document with flows containing blocks and variableContract
            const patchPayload = {
              documentId,
              flows: flowsWithId,
              name: mutatedTemplate.name,
              language: mutatedTemplate.language,
              variableContract: mutatedTemplate.variableContract,
              style: mutatedTemplate.style || {
                fontFamily: 'Arial',
                fontSize: 13,
              }
            };
            
            await patchComplexDocument(patchPayload, null, null, false, false, false);
            
            // Clone and create business logics from template
            if (mutatedTemplate._preservedBusinessLogics) {
              const clonedBusinessLogics = cloneBusinessLogic(mutatedTemplate._preservedBusinessLogics, documentId);
              
              // Create each business logic via API
              for (const businessLogic of clonedBusinessLogics) {
                try {
                  await updateBusinessLogic(businessLogic);
                } catch (blError) {
                  console.warn(`Failed to create business logic for document ${documentId}:`, blError);
                  docResult.errors.push({
                    language,
                    error: `Business logic creation failed: ${blError.message}`,
                  });
                }
              }
            }
            
            // Call updateCloneable with isEditable=true after document creation (for every document)
            try {
              await updateCloneable(documentId, true);
            } catch (cloneableError) {
              console.warn(`Failed to update cloneable for document ${documentId}:`, cloneableError);
              docResult.errors.push({
                language,
                error: `Update cloneable failed: ${cloneableError.message}`,
              });
            }
            
            // Call updateRenderingType to set rendering type to print-cmyk (for every document)
            try {
              await updateRenderingType(documentId, "print_cmyk");
            } catch (renderingTypeError) {
              console.warn(`Failed to update rendering type for document ${documentId}:`, renderingTypeError);
              docResult.errors.push({
                language,
                error: `Update rendering type failed: ${renderingTypeError.message}`,
              });
            }
            
            // Publish document through approval workflow
            const publishResult = await publishDocument(documentId, onProgress);
            
            // Store publishing status in result
            docResult.languages[language] = {
              success: true,
              communicationId,
              publishSuccess: publishResult.success,
              publishStage: publishResult.stage,
              ...(publishResult.errorMessage && { publishError: publishResult.errorMessage }),
            };
            
            // If publishing failed, add to errors but don't mark document creation as failed
            if (!publishResult.success) {
              console.warn(`Failed to publish document ${documentId} at stage ${publishResult.stage}:`, publishResult.errorMessage);
              docResult.errors.push({
                language,
                error: `Publishing failed at ${publishResult.stage}: ${publishResult.errorMessage}`,
              });
            }
            
            // Publishing status already stored above, just increment success count
            result.successfulImports++;
          } else {
            throw new Error(`Failed to create document: ${createResponse.statusCode || 'Unknown error'}`);
          }
        } catch (error) {
          console.error(`Error importing document ${docName} for language ${language}:`, error);
          docResult.languages[language] = {
            success: false,
            error: error.message || 'Unknown error',
          };
          docResult.errors.push({
            language,
            error: error.message || 'Unknown error',
          });
          result.failedImports++;
          result.errors.push({
            document: docName,
            language,
            error: error.message || 'Unknown error',
          });
        }
      }
      
      result.documentResults.push(docResult);
      result.processedDocuments++;
      
      if (onProgress) {
        onProgress({
          stage: 'importing',
          documentIndex: docIndex + 1,
          totalDocuments: documentDefinitions.length,
          processedDocuments: result.processedDocuments,
          successfulImports: result.successfulImports,
          failedImports: result.failedImports,
        });
      }
    };

    // Process documents concurrently (CONCURRENCY_LIMIT at a time)
    const docIndices = Array.from({ length: documentDefinitions.length }, (_, i) => i);
    await processWithConcurrency(docIndices, CONCURRENCY_LIMIT, processDocument);
    
    if (onProgress) {
      onProgress({
        stage: 'complete',
        message: 'Import completed',
        result,
      });
    }
    
    return result;
  } catch (error) {
    console.error('Error importing XML documents:', error);
    if (onProgress) {
      onProgress({
        stage: 'error',
        error: error.message,
      });
    }
    throw error;
  }
};

